import React, { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { navigate } from 'gatsby';
import styled from '@emotion/styled';
import { css, Global } from '@emotion/react';
import { Button } from '@agria/theme/src/components/button/Button';
import { Container } from '@agria/theme/src/components/container/Container';
import { Search as SearchIcon } from '@agria/theme/src/components/icon/icons/Search';
import { Close } from '@agria/theme/src/components/icon/icons/Close';
import { SearchResults } from './SearchResults';
import { SearchFormMemo } from './SearchForm';

const StyledSearchResultsContainer = styled.div`
  ${({ theme: { colors, borderRadii, space } }) => css`
    display: grid;
    gap: ${space.medium};
    padding-inline: ${space.small};
    padding-block: ${space.medium};
    border-radius: ${borderRadii.small};
    border: 2px solid ${colors.brandOne};
    background-color: ${colors.utilityWhite};
    overflow: auto;
  `};
`;

const StyledCloseButton = styled.button`
  ${({ theme: { colors, borderRadii, maxMedia, space } }) => css`
    margin-bottom: 0.5rem;
    grid-row: 1;
    justify-self: end;
    background-color: ${colors.utilityBackground};
    color: ${colors.brandOne};
    border-radius: ${borderRadii.medium};
    display: grid;
    place-items: center;
    padding: 0;
    position: relative;

    svg {
      height: 44px;
      width: 44px;
    }

    &:hover {
      background-color: ${colors.brandOne20};
    }

    @media (max-width: ${maxMedia.xSmall}) {
      margin-right: ${space.medium};
    }
  `};
`;

const StyledSearchDialog = styled.dialog`
  ${({ theme: { colors, layers, maxMedia, media } }) => css`
    z-index: ${layers.modalOverlay};
    background: 0;
    width: 100%;
    max-height: 100dvh; /* Override Firefox default not-quite-100% */
    max-width: unset; /* Override browser default */
    overflow: unset; /* Allow focus-visible to show outside */

    @media (max-width: ${maxMedia.large}) {
      margin-inline: 0;
    }

    @media (min-width: ${media.large}) {
      margin-inline: auto;
    }

    &::backdrop {
      background-color: ${colors.brandOne};
      opacity: 0.9;
    }

    > div {
      padding-block: 2rem;
      pointer-events: none;

      @media (max-width: ${maxMedia.xSmall}) {
        padding-inline: 0;
      }

      @media (min-width: ${media.large}) {
        padding-block: 90px;
      }
    }
  `};
`;

const GlobalSearchModalStyle = css`
  html {
    scrollbar-gutter: stable;

    &:has(${StyledSearchDialog}[open]) {
      overflow: hidden;
    }
  }
`;

const StyledContainerInner = styled.div`
  ${({ theme: { media } }) => css`
    display: grid;

    > * {
      pointer-events: auto;
    }

    // To offset approx the search, mobile, menu buttons plus gaps
    @media (min-width: ${media.medium}) {
      padding-inline: 8rem;
    }

    // To offset approx the gap before, plus padding, plus text of the 'Get a quote' button
    @media (min-width: ${media.large}) {
      padding-inline: calc(5rem + 119px);
    }
  `};
`;

const StyledSearchBarAndResults = styled.div`
  ${({ theme: { colors, borderRadii, media, space } }) => css`
    background: ${colors.utilityBackground};
    display: grid;
    gap: 0.75rem;
    padding-block: ${space.large};
    padding-inline: ${space.medium};
    max-height: calc(100dvh - 7rem);

    @media (min-width: ${media.xSmall}) {
      border-radius: ${borderRadii.medium};
    }

    @media (min-width: ${media.small}) {
      padding-inline: ${space.large};
    }

    @media (min-width: ${media.large}) {
      max-height: calc(100dvh - 12rem);
    }
  `};
`;

interface SearchModalProps {
  location: Location;
}

const SearchModal: FC<SearchModalProps> = ({ location }) => {
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const searchDialogRef = useRef<HTMLDialogElement>(null);
  const blurFocusAfterCloseRef = useRef<boolean>(false);

  const handleOpenClose = () => {
    setSearchQuery('');
    blurFocusAfterCloseRef.current = false;
    setShowSearch(!showSearch);
  };

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setShowResults(event.target.value.length > 2);
  };

  const handleClearInput = () => {
    setSearchQuery('');
    setShowResults(false);
  };

  const handleOnSubmit = () => {
    navigate(`/search?q=${searchQuery.replaceAll(' ', '+')}`);
  };

  const lightDismiss = (event: MouseEvent) => {
    if (event.target === searchDialogRef.current) {
      setShowSearch(false);
    }
  };

  const closeOnEsc = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setShowSearch(false);
    }
  };

  useEffect(() => {
    const searchDialog = searchDialogRef.current;

    const checkRefVisibility = () => {
      if (searchDialog?.checkVisibility() === false) {
        setShowSearch(false);
      }
    };

    searchDialog?.addEventListener('click', lightDismiss);
    searchDialog?.addEventListener('keydown', closeOnEsc);
    window.addEventListener('resize', checkRefVisibility);

    return () => {
      searchDialog?.removeEventListener('click', lightDismiss);
      searchDialog?.removeEventListener('keydown', closeOnEsc);
      window.removeEventListener('resize', checkRefVisibility);
    };
  }, []);

  useEffect(() => {
    if (showSearch) {
      searchDialogRef.current?.showModal();
    } else {
      searchDialogRef.current?.close();
      setShowResults(false);
      setSearchQuery('');

      if (blurFocusAfterCloseRef.current) {
        blurFocusAfterCloseRef.current = false;
        document.querySelector<HTMLElement>('#gatsby-focus-wrapper')?.focus();
      }
    }
  }, [showSearch]);

  useEffect(() => {
    if (showSearch) {
      blurFocusAfterCloseRef.current = true;
      setShowSearch(false);
    }
  }, [location]);

  return (
    <>
      <Button
        a11yTitle="show search"
        iconOnly
        notFullWidth
        onClick={handleOpenClose}
        small
        variant="secondaryOutline"
      >
        <SearchIcon aria-hidden="true" />
      </Button>

      <Global styles={GlobalSearchModalStyle} />

      <StyledSearchDialog ref={searchDialogRef}>
        <Container>
          <StyledContainerInner>
            <StyledCloseButton
              aria-label="Close search overlay"
              onClick={handleOpenClose}
              type="button"
            >
              <Close color="brandOne" aria-hidden="true" />
            </StyledCloseButton>

            <StyledSearchBarAndResults>
              <SearchFormMemo
                inputValue={searchQuery}
                onChange={handleOnChange}
                onClick={handleClearInput}
                onSubmit={handleOnSubmit}
                method="dialog"
              />

              {showResults && (
                <StyledSearchResultsContainer>
                  <SearchResults searchQuery={searchQuery} />
                </StyledSearchResultsContainer>
              )}
            </StyledSearchBarAndResults>
          </StyledContainerInner>
        </Container>
      </StyledSearchDialog>
    </>
  );
};

export { SearchModal };
